import Box from '@mui/material/Box';
import MuiButton from '@mui/material/Button';
import Stack from '@mui/material/Stack';
import React, { useState } from 'react';

import { accentBtnSx, panelFieldGridSx } from '../../../configs/detailPanelStyles';
import { MANUAL_SHIPPING_DATA } from '../../../constants/shipment';
import { SectionLabel } from '../../atoms/SectionLabel';
import { TextField } from '../../atoms/TextField';
import { QuickToggleGroup } from '../../molecules/QuickToggleGroup';

import { ManualShippingData, ManualShippingProps } from '.';

/**
 * Recording a shipment that went out with a courier by hand: which courier, and
 * the consignment number they gave back.
 *
 * The courier picker is a toggle group rather than a row of buttons — one
 * courier is selected at a time, and a row of green buttons read as five
 * separate actions rather than one choice.
 */
export const ManualShipping: React.FC<ManualShippingProps> = ({
  couriers,
  onSave,
  methodToggle,
}) => {
  const [manualShippingData, setManualShippingData] =
    useState<ManualShippingData>(MANUAL_SHIPPING_DATA);

  const courierOptions = couriers.map((courier) => ({
    value: String(courier.id),
    label: courier.name,
  }));

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
      {/* Save sits at the top right of this row rather than at the foot of the
          panel, where toasts were landing on top of it. */}
      <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'flex-start', gap: 2, rowGap: 2 }}>
        {/* Method and courier read left to right as one row of choices, with a
            hairline keeping the spacing even between differently sized groups. */}
        <Box
          sx={{
            display: 'flex',
            flexWrap: 'wrap',
            alignItems: 'stretch',
            gap: 3,
            rowGap: 2,
            '& > * + *': { pl: 3, borderLeft: 1, borderColor: 'v4.border' },
          }}
        >
          {methodToggle}
          <Box>
            <SectionLabel>Courier</SectionLabel>
            {courierOptions.length > 0 ? (
              <QuickToggleGroup
                label="Courier"
                options={courierOptions}
                value={String(manualShippingData.shippingProviderId ?? '')}
                onChange={(value) =>
                  setManualShippingData({
                    ...manualShippingData,
                    shippingProviderId: Number(value),
                  })
                }
              />
            ) : (
              <Box sx={{ fontSize: '13px', color: 'text.secondary' }}>No couriers configured.</Box>
            )}
          </Box>
        </Box>

        <Stack direction="row" justifyContent="flex-end" sx={{ ml: 'auto' }}>
          <MuiButton
            variant="contained"
            size="small"
            sx={accentBtnSx}
            onClick={() => {
              onSave(manualShippingData);
              setManualShippingData(MANUAL_SHIPPING_DATA);
            }}
          >
            Save
          </MuiButton>
        </Stack>
      </Box>

      <Box>
        <SectionLabel>Consignment</SectionLabel>
        <Box sx={panelFieldGridSx}>
          <TextField
            fullWidth
            label="Consignment #"
            value={manualShippingData.connoteNo}
            onChange={(connoteNo: string) =>
              setManualShippingData({ ...manualShippingData, connoteNo })
            }
          />
        </Box>
      </Box>
    </Box>
  );
};
